import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/hooks/useTheme';
import { Icon } from './Icon';

export function ThemeToggle({ className }: { className?: string }) {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={isDark ? '切换到浅色模式' : '切换到深色模式'}
      aria-pressed={isDark}
      title={isDark ? '切换到浅色模式' : '切换到深色模式'}
      className={[
        'inline-flex h-9 w-9 items-center justify-center rounded-btn border border-line bg-surface-1 text-fg-muted transition-colors hover:border-brand hover:text-brand',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Icon name={isDark ? 'sun' : 'moon'} size={17} />
    </button>
  );
}

/** 面包屑。最后一项是当前页，不加链接。 */
export function Breadcrumb({ items }: { items: readonly { name: string; path?: string }[] }) {
  return (
    <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-fg-muted">
      {items.map((item, index) => (
        <span key={`${item.name}-${index}`} className="flex items-center gap-1.5">
          {index > 0 ? <span aria-hidden="true">/</span> : null}
          {item.path ? (
            <Link to={item.path} className="transition-colors hover:text-fg">
              {item.name}
            </Link>
          ) : (
            <span className="text-fg">{item.name}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/** 常见问题。用原生 details，键盘与读屏都不需要额外处理。 */
export function Accordion({ items, className }: { items: readonly { q: string; a: string }[]; className?: string }) {
  return (
    <div className={['flex flex-col gap-3', className].filter(Boolean).join(' ')}>
      {items.map((item) => (
        <details key={item.q} className="group card-surface overflow-hidden px-5 py-4">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-medium text-fg marker:hidden">
            {item.q}
            <Icon
              name="chevronDown"
              size={16}
              className="shrink-0 text-fg-subtle transition-transform group-open:rotate-180"
            />
          </summary>
          <p className="mt-3 text-sm leading-relaxed text-fg-muted">{item.a}</p>
        </details>
      ))}
    </div>
  );
}

/** 空态 / 提示态：404、文章不存在、列表为空都用它。 */
export function EmptyState({
  title,
  text,
  actions,
}: {
  title: string;
  text: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-4 rounded-xl border border-dashed border-line-strong bg-surface-1 p-8">
      <h2 className="text-lg font-semibold text-fg">{title}</h2>
      <p className="max-w-xl text-sm leading-relaxed text-fg-muted">{text}</p>
      {actions}
    </div>
  );
}
